import Pagination from '@/shared/components/Pagination';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import {IFilterGroup, IOrganizationAuctionCategory} from '@/utils/types';
import {Box} from '@mui/material';
import {useState, useMemo, useEffect} from 'react';
import LotItem from '@/components/User/LotItem';
import {auctionItemsListStyle} from './AuctionItemsList.styles';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {clearAuctionData, getAuctionList, getAuctionListSelector} from '@/store/organization-auction';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {formContentStyle} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/FormContent/FormContent.styles';
import Loader from '@/shared/components/Loader';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import {getCurrentEventSelector} from '@/store/organization-event';
import {defaultPageSizeForAttendees} from '@/utils/constants';

const AuctionItemsList = ({
	categories,
	auctionType,
	hasEnded,
}: {
	categories: IOrganizationAuctionCategory[] | [];
	auctionType: string;
	hasEnded?: boolean;
}) => {
	const dispatch = useAppDispatch();

	const event = useAppSelector(getCurrentEventSelector);
	const auctionList = useAppSelector(getAuctionListSelector);

	const [searchValue, setSearchValue] = useState('');
	const [page, setPage] = useState<number>(1);
	const [category, setCategory] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		getAuctionListData();
	}, [page, category, searchValue, event?.id]);

	const categoryIds = useMemo(() => {
		if (category.length === 0) return null;
		return categories.filter((c) => category.includes(c.name)).map((c) => c.id);
	}, [categories, category]);

	useEffect(() => {
		if (!event?.id) setIsLoading(true);
	}, [event?.id]);

	const getAuctionListData = async () => {
		setIsLoading(true);
		dispatch(clearAuctionData());
		try {
			const reqBody = {
				eventId: event?.id as number,
				page,
				pageSize: 8,
				searchName: searchValue,
				categoryIds,
				auctionType,
			};

			await dispatch(getAuctionList(reqBody)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handlePressEnter = () => {};

	const options = useMemo(
		() => [
			{title: 'All Categories', isCheckBox: false},
			...categories.map((category) => ({
				title: category.name,
				isCheckBox: true,
				id: category.id,
			})),
		],
		[categories]
	);

	const filteredOptions = useMemo(() => {
		return options.map((option) => ({
			...option,
			isSelected: option.title === 'All Categories' ? category.length === 0 : category.includes(option.title),
		}));
	}, [category, options]);

	const filterOptionData: IFilterGroup[] = [
		{
			title: 'Categories',
			options: filteredOptions,
		},
	];

	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		setPage(1);
	};

	const handleFilterChange = (groupTitle: string, optionTitle: string) => {
		setPage(1);
		if (optionTitle === 'All Categories') return setCategory([]);

		setCategory((prevState) =>
			prevState.includes(optionTitle) ? prevState.filter((item) => item !== optionTitle) : [...prevState, optionTitle]
		);
	};

	const handlePaginationChange = (step: number) => {
		setPage(step);
	};

	return (
		<Box sx={auctionItemsListStyle.wrapperList}>
			<Box>
				<SearchHeader
					page={auctionList?.currentPage || 0}
					items={
						auctionList && auctionList?.totalCount < defaultPageSizeForAttendees
							? auctionList?.totalCount
							: defaultPageSizeForAttendees
					}
					// items={auctionList?.pageSize || 0}
					searchPlaceholder="Search items"
					total={auctionList?.totalCount}
					onPressEnter={handlePressEnter}
					onSearchChange={handleSearchChange}
					onFilterChange={handleFilterChange}
					filterOption={filterOptionData}
					value={searchValue}
				/>
			</Box>

			<Box sx={auctionItemsListStyle.lotList}>
				{isLoading ? (
					<Box sx={{height: 'calc(100vh - 270px)'}}>
						<Loader sx={formContentStyle.loader} size={50} />
					</Box>
				) : auctionList?.data && auctionList?.data.length > 0 ? (
					auctionList?.data.map((item) => {
						return (
							<LotItem
								key={item.id}
								data={item}
								auctionType={auctionType}
								getListData={getAuctionListData}
								hasEnded={hasEnded}
							/>
						);
					})
				) : (
					<Box sx={{gridColumn: '1 / -1'}}>
						<EmptyTableData />
					</Box>
				)}
			</Box>
			{auctionList?.data && auctionList?.data.length > 0 && (
				<Box sx={auctionItemsListStyle.pagination}>
					<Pagination
						page={auctionList?.currentPage || 0}
						pageSize={auctionList?.pageSize || 0}
						total={auctionList?.totalCount || 0}
						onChange={(step) => handlePaginationChange(step)}
					/>
				</Box>
			)}
		</Box>
	);
};

export default AuctionItemsList;
